/**
 * "How are you feeling?" — trauma-informed emotion pathway.
 *
 * Content rules (non-negotiable):
 *   • Educational and reflective only. Never diagnostic, never causal.
 *   • Always exploratory wording: "sometimes", "this may or may not fit you".
 *   • Practices point only at pages that already exist on Recovery Trauma™.
 */

export type NeedKey =
  | "safety"
  | "self-compassion"
  | "connection"
  | "space"
  | "comfort"
  | "validation"
  | "calm"
  | "rest"
  | "boundaries"
  | "reassurance"
  | "expression"
  | "self-acceptance"
  | "grounding"
  | "support";

export const NEEDS: { key: NeedKey; label: string; line: string; icon: string; color: string }[] = [
  { key: "safety", label: "Safety", line: "Something steady that helps your body know it is not in danger right now.", icon: "shield", color: "#5B8FD6" },
  { key: "self-compassion", label: "Compassion", line: "To meet yourself the way you'd meet someone you love.", icon: "heart-handshake", color: "#E8709A" },
  { key: "connection", label: "Connection", line: "To feel less alone with this.", icon: "users", color: "#F0954D" },
  { key: "space", label: "Space", line: "Room to breathe, without anyone needing anything from you.", icon: "leaf", color: "#57B87E" },
  { key: "comfort", label: "Comfort", line: "Warmth, softness, something gentle for the body.", icon: "hand-heart", color: "#EE8474" },
  { key: "validation", label: "Validation", line: "For this to make sense, rather than be argued with.", icon: "message-circle", color: "#A972C4" },
  { key: "calm", label: "Calm", line: "A slower gear — less noise inside and around you.", icon: "waves", color: "#3FB6B0" },
  { key: "rest", label: "Rest", line: "Permission to stop, slow down, and do less for a while.", icon: "moon", color: "#8A6FD1" },
  { key: "boundaries", label: "Boundaries", line: "A clearer line around what is and isn't okay for you.", icon: "shield-half", color: "#DE5C5C" },
  { key: "reassurance", label: "Reassurance", line: "A kind, steady voice — including your own.", icon: "sparkles", color: "#F2C14E" },
  { key: "expression", label: "Expression", line: "To get this out of you — in words, sound, movement or colour.", icon: "palette", color: "#4FA9C9" },
  { key: "grounding", label: "Grounding", line: "To come back into the present moment and into your body.", icon: "anchor", color: "#4C8FB0" },
  { key: "support", label: "Support", line: "Not to be alone with this — a person, a group, or a practitioner.", icon: "life-buoy", color: "#D97A9B" },
  { key: "self-acceptance", label: "Self-acceptance", line: "Room to be as you are today, without earning it.", icon: "sprout", color: "#86BB5B" },
];

export type Practice = {
  label: string;
  to: string;
  blurb: string;
  needs: NeedKey[];
  /** Guests see this as a locked invitation rather than a dead link. */
  memberOnly?: boolean;
};

export type CoreFeeling = {
  key: string;
  label: string;
  icon: string;
  /** Feature-specific accent used by the emotion wheel. */
  color: string;
  /** Short line shown on the wheel/card. */
  hint: string;
  secondary: string[];
  feelsLike: string;
  connectedTo: string[];
  notice: { emotional: string[]; behavioural: string[]; body: string[] };
  prompts: string[];
  practices: Practice[];
};

const P = {
  grounding: { label: "Grounding practice", to: "/grounding", blurb: "Simple ways to bring your attention back to the present.", needs: ["safety", "space", "grounding"] as NeedKey[] },
  gentleGrounding: { label: "Gentle sensory grounding", to: "/grounding", blurb: "Low-demand grounding that asks very little of you.", needs: ["safety", "comfort", "grounding"] as NeedKey[] },
  breathing: { label: "Breathing exercises", to: "/breathing", blurb: "Guided breath patterns with a long, slow out-breath.", needs: ["calm", "safety", "rest"] as NeedKey[] },
  somatic: { label: "Somatic practice", to: "/somatic", blurb: "Movement and body-based release, at your own pace.", needs: ["expression", "space"] as NeedKey[] },
  sounds: { label: "Calming sounds", to: "/sounds", blurb: "Soundscapes you can rest with for a few minutes.", needs: ["calm", "comfort", "rest"] as NeedKey[] },
  meditations: { label: "A short meditation", to: "/meditations", blurb: "Gentle guided audio, from a couple of minutes upward.", needs: ["calm", "rest", "comfort"] as NeedKey[] },
  tapping: { label: "EFT tapping", to: "/tapping", blurb: "Tapping sequences for moments of intensity.", needs: ["safety", "expression"] as NeedKey[] },
  journal: { label: "Guided journal", to: "/guided-journal", blurb: "Prompts that hold you while you write.", needs: ["expression", "validation"] as NeedKey[], memberOnly: true },
  selfLove: { label: "Self-compassion practice", to: "/self-love", blurb: "Ways of speaking to yourself with more warmth.", needs: ["self-compassion", "reassurance", "self-acceptance"] as NeedKey[], memberOnly: true },
  innerChild: { label: "Inner-child work", to: "/inner-child", blurb: "Tending to the younger part of you that may be carrying this.", needs: ["comfort", "self-compassion"] as NeedKey[], memberOnly: true },
  innerCritic: { label: "Understanding your inner critic", to: "/learn/inner-critic", blurb: "Where that harsh inner voice can come from.", needs: ["validation", "self-compassion", "self-acceptance"] as NeedKey[] },
  shame: { label: "Shame and trauma", to: "/learn/shame-based-trauma", blurb: "How shame settles in the body, and how it eases.", needs: ["validation"] as NeedKey[] },
  boundaries: { label: "Boundaries", to: "/learn/boundaries", blurb: "What boundaries are, and gentle ways to set them.", needs: ["boundaries", "safety"] as NeedKey[] },
  attachment: { label: "Attachment patterns", to: "/learn/attachment", blurb: "How early bonds shape the way closeness feels now.", needs: ["validation", "connection", "support"] as NeedKey[] },
  grief: { label: "Grief and trauma", to: "/learn/grief-trauma", blurb: "Grief that doesn't move in a straight line.", needs: ["validation", "comfort"] as NeedKey[] },
  window: { label: "Window of tolerance", to: "/learn/window-of-tolerance", blurb: "Why you tip into too-much or too-little, and what helps.", needs: ["safety", "validation"] as NeedKey[] },
  quickResets: { label: "Quick resets", to: "/quick-resets", blurb: "Two-minute practices for when capacity is low.", needs: ["calm", "rest", "space"] as NeedKey[] },
  nervousSystem: { label: "Nervous-system reset", to: "/nervous-system-reset", blurb: "A longer sequence to bring the system down a gear.", needs: ["calm", "safety", "rest"] as NeedKey[], memberOnly: true },
  safety: { label: "Safety and stabilisation", to: "/safety-stabilisation", blurb: "Building a felt sense of steadiness first.", needs: ["safety", "grounding", "support"] as NeedKey[], memberOnly: true },
  cbtCards: { label: "Thought cards", to: "/cbt-thought-cards", blurb: "Gently questioning thoughts that feel like facts.", needs: ["validation", "reassurance"] as NeedKey[], memberOnly: true },
  affirmations: { label: "Affirmations", to: "/affirmations", blurb: "Words you can borrow until your own feel true.", needs: ["reassurance", "self-compassion", "self-acceptance"] as NeedKey[] },
  creative: { label: "Creative reflection", to: "/creative-reflection", blurb: "Letting it out without needing the right words.", needs: ["expression"] as NeedKey[], memberOnly: true },
  artBoard: { label: "Art board", to: "/art-board", blurb: "Colour, draw, and see what appears.", needs: ["expression", "comfort", "calm"] as NeedKey[], memberOnly: true },
  community: { label: "The community", to: "/community", blurb: "People who understand, at your own distance.", needs: ["connection", "validation", "support"] as NeedKey[] },
  bodyAwareness: { label: "Body awareness", to: "/somatic", blurb: "Very small check-ins with the body, no pressure.", needs: ["safety", "space", "grounding"] as NeedKey[] },
} satisfies Record<string, Practice>;

export const CORE_FEELINGS: CoreFeeling[] = [
  {
    key: "shame",
    label: "Shame",
    icon: "user-round",
    color: "#F2C14E",
    hint: "Embarrassed · Exposed",
    secondary: ["Embarrassed", "Exposed", "Inadequate", "Judged", "Unworthy"],
    feelsLike:
      "Shame often feels like there is something wrong with you, rather than with something that happened. It can arrive as heat, a wish to hide, or a sudden pull to disappear from the room.",
    connectedTo: [
      "Being criticised, mocked or corrected in front of others",
      "Growing up where mistakes were met with disapproval rather than repair",
      "Being blamed for things that were not yours to carry",
      "Feeling exposed after sharing something personal",
      "Comparing yourself with other people today",
    ],
    notice: {
      emotional: ["A harsh inner voice", "Wanting to be invisible", "Feeling small or exposed"],
      behavioural: ["Withdrawing or cancelling plans", "Over-apologising", "Replaying what you said"],
      body: ["Heat in the face or chest", "Curling inward", "Difficulty meeting someone's eyes"],
    },
    prompts: [
      "When did you first notice this feeling today?",
      "Whose voice does the harsh thought sound like?",
      "If a friend told you exactly this about themselves, what would you say to them?",
    ],
    practices: [P.selfLove, P.innerChild, P.shame, P.journal, P.tapping, P.grounding],
  },
  {
    key: "guilt",
    label: "Guilt",
    icon: "hand-heart",
    color: "#F0954D",
    hint: "Regretful · Responsible",
    secondary: ["Regretful", "Responsible", "Self-blaming", "Remorseful", "“I should have…”"],
    feelsLike:
      "Guilt tends to focus on something you did or didn't do. It can be useful when it points to a repair — and heavy when it attaches itself to things that were never in your control.",
    connectedTo: [
      "Saying no, or putting your own needs first",
      "Being made responsible for other people's feelings early on",
      "Resting when part of you believes you should be productive",
      "Something you'd do differently if you could",
    ],
    notice: {
      emotional: ["Going over the same moment", "A pull to fix or explain", "Difficulty accepting kindness"],
      behavioural: ["Over-giving", "Apologising for things that aren't yours", "Struggling to rest"],
      body: ["Heaviness in the stomach", "Restlessness", "Tension across the shoulders"],
    },
    prompts: [
      "What exactly are you holding yourself responsible for?",
      "How much of it was actually within your control?",
      "Is there a repair to make, or is this asking for self-forgiveness?",
    ],
    practices: [P.cbtCards, P.journal, P.selfLove, P.grounding, P.boundaries],
  },
  {
    key: "sad",
    label: "Sad",
    icon: "cloud-rain",
    color: "#EE8474",
    hint: "Lonely · Grieving",
    secondary: ["Lonely", "Grieving", "Heartbroken", "Disappointed", "Hopeless"],
    feelsLike:
      "Sadness can be quiet and slow, or arrive in waves. It often carries something that mattered — a loss, an ending, or a need that went unmet for a long time.",
    connectedTo: [
      "A loss, recent or long ago",
      "An anniversary or a date your body remembers",
      "Something ending, changing, or not turning out as hoped",
      "Tiredness or a long stretch of holding a lot",
    ],
    notice: {
      emotional: ["Tearfulness, or being unable to cry", "Low motivation", "Tenderness"],
      behavioural: ["Sleeping more or less", "Pulling away from people", "Losing interest in usual things"],
      body: ["Heaviness in the chest", "A lump in the throat", "Low energy"],
    },
    prompts: [
      "If this sadness could speak, what would it want you to know?",
      "What, or who, does it seem to be about?",
      "What would comfort look like today, even in a small way?",
    ],
    practices: [P.selfLove, P.grief, P.journal, P.sounds, P.innerChild, P.meditations],
  },
  {
    key: "anxious",
    label: "Anxious",
    icon: "brain",
    color: "#E8709A",
    hint: "Worried · Restless",
    secondary: ["Worried", "Restless", "Panicky", "On edge", "Overthinking"],
    feelsLike:
      "Anxiety is often the body preparing for something. It can look like racing thoughts, an inability to settle, or a sense that something bad is about to happen even when nothing has.",
    connectedTo: [
      "Uncertainty, waiting, or something unresolved",
      "Growing up somewhere unpredictable, where staying alert helped",
      "Too much input — noise, people, screens, demands",
      "Not enough sleep, food or recovery time",
    ],
    notice: {
      emotional: ["Racing or looping thoughts", "Scanning for what could go wrong", "Irritability"],
      behavioural: ["Checking, planning, rehearsing", "Avoiding things", "Difficulty sitting still"],
      body: ["Fast heartbeat", "Shallow breath", "Tight chest or stomach"],
    },
    prompts: [
      "What is your body preparing for right now?",
      "What is actually happening in this room, in this minute?",
      "What is one thing within your control today?",
    ],
    practices: [P.breathing, P.grounding, P.somatic, P.sounds, P.tapping, P.window],
  },
  {
    key: "angry",
    label: "Angry",
    icon: "flame",
    color: "#DE5C5C",
    hint: "Frustrated · Betrayed",
    secondary: ["Frustrated", "Betrayed", "Resentful", "Powerless", "Violated"],
    feelsLike:
      "Anger often shows up where a line has been crossed. It can be hot and fast, or slow and simmering. It isn't a problem to be removed — it's usually information about something that mattered.",
    connectedTo: [
      "A boundary being crossed, recently or repeatedly",
      "Being unheard, dismissed or treated unfairly",
      "Anger having been unsafe to express earlier in life",
      "Feeling powerless in a situation you can't change",
    ],
    notice: {
      emotional: ["Irritation at small things", "Wanting to argue, or to leave", "Resentment building"],
      behavioural: ["Snapping or going silent", "Restless energy", "Replaying the conversation"],
      body: ["Heat, clenched jaw or fists", "Tight shoulders", "A surge of energy"],
    },
    prompts: [
      "What line felt crossed?",
      "If your anger were protecting something, what would it be protecting?",
      "What would you want to say if it were completely safe to say it?",
    ],
    practices: [P.somatic, P.grounding, P.journal, P.boundaries, P.breathing, P.artBoard],
  },
  {
    key: "lonely",
    label: "Lonely",
    icon: "users",
    color: "#A972C4",
    hint: "Isolated · Disconnected",
    secondary: ["Isolated", "Disconnected", "Unseen", "Unheard", "Abandoned"],
    feelsLike:
      "Loneliness isn't only about being on your own. It can be strongest in a room full of people, when the parts of you that most want to be known stay hidden.",
    connectedTo: [
      "Not being able to share what is really going on",
      "Having learned early that needing people wasn't safe",
      "A change in a friendship, family or relationship",
      "Being physically alone for a long stretch",
    ],
    notice: {
      emotional: ["An ache for closeness", "Assuming you're a burden", "Envy of other people's ease"],
      behavioural: ["Scrolling more", "Turning down invitations", "Waiting for someone else to reach out"],
      body: ["Hollowness in the chest", "Tiredness", "Restlessness in the evenings"],
    },
    prompts: [
      "Is this a wish for company, or to be truly known?",
      "Who feels even slightly safe to reach towards?",
      "What has stopped you reaching out before?",
    ],
    practices: [P.community, P.selfLove, P.innerChild, P.sounds, P.journal, P.attachment],
  },
  {
    key: "rejected",
    label: "Rejected",
    icon: "heart-crack",
    color: "#8A6FD1",
    hint: "Left out · Unwanted",
    secondary: ["Left out", "Unwanted", "Excluded", "Abandoned", "Different"],
    feelsLike:
      "Rejection can land in the body like a physical wound. Even a small moment — an unanswered message, being left out — can feel much bigger than it looks from the outside.",
    connectedTo: [
      "A recent moment of being left out or turned down",
      "Earlier experiences of being excluded or not chosen",
      "Feeling different in spaces that expected sameness",
      "A relationship where love felt conditional",
    ],
    notice: {
      emotional: ["Sharp self-criticism", "Wanting to withdraw first", "Searching for what you did wrong"],
      behavioural: ["Re-reading messages", "Pulling away pre-emptively", "Trying harder to be liked"],
      body: ["A drop in the stomach", "Tight throat", "Heat in the face"],
    },
    prompts: [
      "What did this moment seem to confirm about you?",
      "Is that belief a fact, or an old conclusion?",
      "How old does this feeling feel?",
    ],
    practices: [P.innerChild, P.attachment, P.selfLove, P.journal, P.affirmations],
  },
  {
    key: "overwhelmed",
    label: "Overwhelmed",
    icon: "waves",
    color: "#5B8FD6",
    hint: "Exhausted · Stretched",
    secondary: ["Exhausted", "Stretched", "Too much", "Unable to cope", "Mentally overloaded"],
    feelsLike:
      "Overwhelm is what happens when there is more coming in than there is capacity to hold. Everything can feel urgent, and nothing can feel possible.",
    connectedTo: [
      "Too many demands at once, with no gaps between them",
      "Sensory input — noise, light, people, screens",
      "Carrying responsibility for other people's needs",
      "Not enough rest for a long time",
    ],
    notice: {
      emotional: ["Tearfulness or blankness", "Everything feeling urgent", "Wanting to escape"],
      behavioural: ["Freezing on simple tasks", "Avoiding messages", "Doing everything at once"],
      body: ["Tight chest", "Buzzing or heaviness", "Foggy thinking"],
    },
    prompts: [
      "What is the smallest thing that would take a little weight off?",
      "What can wait until tomorrow, even if part of you disagrees?",
      "What are you holding that isn't yours?",
    ],
    practices: [P.quickResets, P.breathing, P.nervousSystem, P.grounding, P.window, P.sounds],
  },
  {
    key: "numb",
    label: "Numb",
    icon: "snowflake",
    color: "#4FA9C9",
    hint: "Empty · Disconnected",
    secondary: ["Empty", "Disconnected", "Detached", "Shut down", "Going through the motions"],
    feelsLike:
      "Numbness is often protective. When feeling everything would be too much, the system can turn the volume down. It can be lonely, and it usually isn't something you chose.",
    connectedTo: [
      "A long period of holding a great deal",
      "Something recent that was hard to take in",
      "Times when feeling was not safe or not allowed",
      "Deep tiredness or burnout",
    ],
    notice: {
      emotional: ["Flatness", "Watching yourself from outside", "Not caring, and minding that you don't"],
      behavioural: ["Going through routines automatically", "Scrolling for long stretches", "Little appetite for anything"],
      body: ["Feeling far away from your body", "Muffled sensation", "Heaviness or weightlessness"],
    },
    prompts: [
      "When did you last feel something clearly?",
      "What might your system be protecting you from feeling?",
      "What is one very small sensation you can notice right now?",
    ],
    practices: [P.gentleGrounding, P.bodyAwareness, P.sounds, P.creative, P.artBoard, P.somatic],
  },
  {
    key: "afraid",
    label: "Afraid",
    icon: "shield",
    color: "#3FB6B0",
    hint: "Scared · Unsafe",
    secondary: ["Scared", "Unsafe", "Vulnerable", "Threatened", "Helpless"],
    feelsLike:
      "Fear is the body's alarm. Sometimes it is responding to now, and sometimes to something it remembers. Either way, the alarm is real even when the danger has passed.",
    connectedTo: [
      "Something in the present that genuinely feels unsafe",
      "A reminder — a sound, smell, tone of voice — of an earlier time",
      "Being somewhere unfamiliar or unpredictable",
      "Anticipating something you can't control",
    ],
    notice: {
      emotional: ["Dread", "Hypervigilance", "Difficulty trusting reassurance"],
      behavioural: ["Checking exits or locks", "Avoiding places or people", "Startling easily"],
      body: ["Racing heart", "Shaking", "Holding your breath"],
    },
    prompts: [
      "Is this fear about now, or about something your body remembers?",
      "What would help you feel one per cent safer in this room?",
      "Who or what helps you feel steadier?",
    ],
    practices: [P.grounding, P.safety, P.breathing, P.window, P.sounds],
  },
  {
    key: "disappointed",
    label: "Disappointed",
    icon: "leaf",
    color: "#57B87E",
    hint: "Let down · Discouraged",
    secondary: ["Let down", "Discouraged", "Deflated", "Unmet needs", "Hopeful for more"],
    feelsLike:
      "Disappointment sits between sadness and anger. It often shows up where you hoped for something — and hoping, after everything, took courage.",
    connectedTo: [
      "Someone not showing up the way you needed",
      "An outcome that didn't match the effort you put in",
      "A pattern repeating when you hoped it wouldn't",
      "Expectations that were never spoken aloud",
    ],
    notice: {
      emotional: ["Deflation", "Cynicism creeping in", "Reluctance to hope again"],
      behavioural: ["Lowering your expectations of people", "Withdrawing effort", "Going quiet"],
      body: ["A sinking feeling", "Tiredness", "Tightness in the chest"],
    },
    prompts: [
      "What were you hoping for?",
      "Was that hope ever shared with the other person?",
      "What does this disappointment tell you about what matters to you?",
    ],
    practices: [P.journal, P.selfLove, P.creative, P.sounds, P.boundaries],
  },
  {
    key: "unworthy",
    label: "Unworthy",
    icon: "user",
    color: "#86BB5B",
    hint: "Not enough · Inadequate",
    secondary: ["Not enough", "Inadequate", "Unlovable", "Not good enough", "Like a burden"],
    feelsLike:
      "Feeling unworthy often sounds like a fact rather than a feeling. It can be one of the oldest beliefs a person carries, and it tends to speak loudest when you are tired.",
    connectedTo: [
      "Love or approval that had to be earned",
      "Being compared with others",
      "Repeated criticism, or praise only for achievement",
      "Being treated as an inconvenience when you had needs",
    ],
    notice: {
      emotional: ["A relentless inner critic", "Discounting good things", "Difficulty accepting help"],
      behavioural: ["Over-working", "People-pleasing", "Not asking for what you need"],
      body: ["Slumped posture", "Heaviness", "Tension in the jaw or shoulders"],
    },
    prompts: [
      "Whose standard are you measuring yourself against?",
      "When did you first learn you had to earn your place?",
      "What would change if your worth were not up for debate?",
    ],
    practices: [P.innerCritic, P.innerChild, P.selfLove, P.affirmations, P.journal],
  },
];

/** Body-first pathway for "I don't know what I'm feeling". */
export type BodySign = {
  key: string;
  label: string;
  line: string;
  color: string;
  /** Feelings this *sometimes* travels with — offered, never asserted. */
  maybe: string[];
};

export const BODY_SIGNS: BodySign[] = [
  { key: "tight-chest", label: "Tight chest", line: "A band across the chest, or breath that won't go all the way down.", color: "#E8709A", maybe: ["anxious", "afraid", "sad"] },
  { key: "heavy", label: "Heavy", line: "Limbs and body feeling weighted, slow, hard to move.", color: "#8A6FD1", maybe: ["sad", "overwhelmed", "unworthy"] },
  { key: "restless", label: "Restless", line: "Unable to settle, needing to move or do something.", color: "#F0954D", maybe: ["anxious", "angry", "overwhelmed"] },
  { key: "shaky", label: "Shaky", line: "Trembling hands, wobbly legs, a fizzing under the skin.", color: "#F2C14E", maybe: ["afraid", "anxious", "angry"] },
  { key: "racing-heart", label: "Racing heart", line: "A fast or pounding heartbeat with nothing obvious happening.", color: "#DE5C5C", maybe: ["anxious", "afraid", "angry"] },
  { key: "lump-throat", label: "Lump in throat", line: "A closing throat, words that won't come out.", color: "#EE8474", maybe: ["sad", "shame", "lonely"] },
  { key: "tense-jaw", label: "Tense jaw", line: "Jaw, shoulders or stomach gripping without you deciding to.", color: "#A972C4", maybe: ["anxious", "angry", "afraid"] },
  { key: "nauseous", label: "Sick or nauseous", line: "A turning stomach, no appetite, or a queasy edge.", color: "#86BB5B", maybe: ["anxious", "shame", "afraid"] },
  { key: "hot", label: "Hot", line: "Heat rising in the face, chest or hands.", color: "#F5A25D", maybe: ["shame", "angry", "anxious"] },
  { key: "cold", label: "Cold", line: "Chilled, shivery, or as though warmth has drained away.", color: "#5B8FD6", maybe: ["afraid", "numb", "sad"] },
  { key: "empty", label: "Empty", line: "A hollow space where feeling usually is.", color: "#4FA9C9", maybe: ["numb", "lonely", "sad"] },
  { key: "disconnected", label: "Disconnected", line: "Watching yourself from a distance, or the room feeling unreal.", color: "#3FB6B0", maybe: ["numb", "overwhelmed", "afraid"] },
  { key: "numb", label: "Numb", line: "Little sensation at all, as though the volume is turned down.", color: "#7FC8D6", maybe: ["numb", "overwhelmed", "sad"] },
  { key: "nothing", label: "I can't notice anything", line: "That's a completely valid answer. Noticing takes practice, and it can come back slowly.", color: "#57B87E", maybe: [] },
];

/** Fallback practices for the body-first route when no feeling is chosen. */
export const BODY_FALLBACK_PRACTICES: Practice[] = [P.gentleGrounding, P.breathing, P.bodyAwareness, P.sounds];

export function getFeeling(key: string): CoreFeeling | undefined {
  return CORE_FEELINGS.find((f) => f.key === key);
}

/** Feeling first, then re-ordered by the needs the member chose. */
export function orderPractices(practices: Practice[], needs: NeedKey[]): Practice[] {
  if (needs.length === 0) return practices;
  return [...practices]
    .map((p, i) => ({ p, i, score: p.needs.filter((n) => needs.includes(n)).length }))
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.p);
}

export const SAFETY_NOTE =
  "This is for education and self-reflection only. It isn't an assessment, a diagnosis or therapy, and it can't tell you why you feel something \u2014 only you can know that.";

/** Accent for the "I don't know what I'm feeling" segment. */
export const UNKNOWN_COLOR = "#7FC8D6";

/** Soft tint of an emotion colour, for fills. Theme-aware: mixes toward a dark surface in Night Mode. */
export function tint(color: string, pct: number): string {
  return `color-mix(in oklab, ${color} ${pct}%, var(--emotion-tint-base, white))`;
}

/** Emotion colour mixed for text/outlines that must stay readable on the tint. Theme-aware. */
export function ink(color: string): string {
  return `color-mix(in oklab, ${color} var(--emotion-ink-mix, 62%), var(--emotion-ink-base, #2A1E12))`;
}
